import type { RoofFaceModuleData, SavedRoofModulesLayout } from '../../../../../@types/types';

type ViewerRoof = {
    roofFaceId?: string;
    userData?: {
        roof_tag_id?: number;
        roof_number_from_aurora?: number;
        nameOverride?: number;
    };
};

type LayoutRoofSummary = {
    roofFaceId?: string;
    roofNum?: unknown;
};

export function readViewerRoofStableKeys(roof: ViewerRoof | null | undefined): {
    roofTagId?: number;
    roofNumber?: number;
    nameOverride?: number;
} {
    const userData = roof?.userData ?? {};
    const roofTagId =
        typeof userData.roof_tag_id === 'number' && Number.isFinite(userData.roof_tag_id)
            ? userData.roof_tag_id
            : undefined;
    const roofNumber =
        typeof userData.roof_number_from_aurora === 'number' &&
        Number.isFinite(userData.roof_number_from_aurora)
            ? userData.roof_number_from_aurora
            : roofTagId;
    const nameOverride =
        typeof userData.nameOverride === 'number' && Number.isFinite(userData.nameOverride)
            ? userData.nameOverride
            : undefined;
    return { roofTagId, roofNumber, nameOverride };
}

function numOrUndefined(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Copy stable roof keys onto layout rows while the live viewer still matches cached face ids. */
export function enrichModulesLayoutWithStableRoofKeys(
    viewer: { roofMeshArr?: ViewerRoof[] } | null | undefined,
    layout: SavedRoofModulesLayout | null | undefined,
): SavedRoofModulesLayout {
    if (!viewer?.roofMeshArr?.length || !Array.isArray(layout) || !layout.length) {
        return layout ?? [];
    }

    const liveByFaceId = new Map(
        viewer.roofMeshArr
            .filter((roof) => typeof roof.roofFaceId === 'string' && roof.roofFaceId.length > 0)
            .map((roof) => [roof.roofFaceId as string, roof]),
    );

    return layout.map((entry) => {
        const roof = liveByFaceId.get(entry.roofFaceId);
        if (!roof) return entry;
        const keys = readViewerRoofStableKeys(roof);
        return {
            ...entry,
            roofTagId: keys.roofTagId ?? entry.roofTagId,
            roofNumber: keys.roofNumber ?? entry.roofNumber,
            nameOverride: keys.nameOverride ?? entry.nameOverride,
        };
    });
}

function findViewerRoofForLayoutEntry(
    viewerRoofs: ViewerRoof[],
    entry: RoofFaceModuleData,
    staleFaceIdToRoofNum?: Map<string, number>,
): ViewerRoof | undefined {
    if (entry.roofFaceId) {
        const direct = viewerRoofs.find((roof) => roof.roofFaceId === entry.roofFaceId);
        if (direct) return direct;
    }

    const roofTagId = numOrUndefined(entry.roofTagId);
    if (roofTagId != null) {
        const byTag = viewerRoofs.find((roof) => roof.userData?.roof_tag_id === roofTagId);
        if (byTag) return byTag;
    }

    const roofNumber = numOrUndefined(entry.roofNumber);
    if (roofNumber != null) {
        const byNumber = viewerRoofs.find(
            (roof) =>
                roof.userData?.roof_number_from_aurora === roofNumber ||
                roof.userData?.roof_tag_id === roofNumber,
        );
        if (byNumber) return byNumber;
    }

    const nameOverride = numOrUndefined(entry.nameOverride);
    if (nameOverride != null) {
        const byName = viewerRoofs.find((roof) => roof.userData?.nameOverride === nameOverride);
        if (byName) return byName;
    }

    if (entry.roofFaceId && staleFaceIdToRoofNum?.has(entry.roofFaceId)) {
        const roofNum = staleFaceIdToRoofNum.get(entry.roofFaceId)!;
        const bySummaryNum = viewerRoofs.find(
            (roof) =>
                roof.userData?.roof_tag_id === roofNum ||
                roof.userData?.roof_number_from_aurora === roofNum ||
                roof.userData?.nameOverride === roofNum,
        );
        if (bySummaryNum) return bySummaryNum;
    }

    return undefined;
}

function buildStaleFaceIdToRoofNumMap(
    layoutRoofSummaries?: LayoutRoofSummary[] | null,
): Map<string, number> {
    const out = new Map<string, number>();
    for (const row of layoutRoofSummaries ?? []) {
        const faceId = typeof row.roofFaceId === 'string' ? row.roofFaceId : '';
        const roofNum = numOrUndefined(row.roofNum);
        if (faceId && roofNum != null) out.set(faceId, roofNum);
    }
    return out;
}

/** When only module counts differ between roofs, map by unique counts as a last resort. */
function buildLegacyModuleCountMap(
    viewerRoofs: ViewerRoof[],
    entries: RoofFaceModuleData[],
    usedLiveFaceIds: Set<string>,
): Map<string, string> {
    const out = new Map<string, string>();
    const unmatched = entries.filter(
        (entry) =>
            entry.roofFaceId &&
            !viewerRoofs.some((roof) => roof.roofFaceId === entry.roofFaceId) &&
            (entry.moduleArr?.length ?? 0) > 0,
    );
    if (!unmatched.length) return out;

    const countFrequency = new Map<number, number>();
    for (const entry of unmatched) {
        const count = entry.moduleArr?.length ?? 0;
        countFrequency.set(count, (countFrequency.get(count) ?? 0) + 1);
    }

    for (const entry of unmatched) {
        const staleId = entry.roofFaceId;
        const count = entry.moduleArr?.length ?? 0;
        if (!staleId || count <= 0) continue;
        if ((countFrequency.get(count) ?? 0) !== 1) continue;

        const openRoofs = viewerRoofs.filter(
            (roof) => typeof roof.roofFaceId === 'string' && !usedLiveFaceIds.has(roof.roofFaceId),
        );
        if (openRoofs.length !== 1) continue;

        out.set(staleId, openRoofs[0].roofFaceId as string);
        usedLiveFaceIds.add(openRoofs[0].roofFaceId as string);
    }

    return out;
}

/** Rebind cached module placements to the roofs currently loaded in the viewer. */
export function remapModulesLayoutToViewer(
    viewer: { roofMeshArr?: ViewerRoof[] } | null | undefined,
    layout: SavedRoofModulesLayout | null | undefined,
    layoutRoofSummaries?: LayoutRoofSummary[] | null,
): SavedRoofModulesLayout {
    return remapModulesLayoutToViewerWithMap(viewer, layout, layoutRoofSummaries).layout;
}

export function remapModulesLayoutToViewerWithMap(
    viewer: { roofMeshArr?: ViewerRoof[] } | null | undefined,
    layout: SavedRoofModulesLayout | null | undefined,
    layoutRoofSummaries?: LayoutRoofSummary[] | null,
): { layout: SavedRoofModulesLayout; staleToLiveFaceIds: Map<string, string> } {
    const staleToLiveFaceIds = new Map<string, string>();
    if (!viewer?.roofMeshArr?.length || !Array.isArray(layout) || !layout.length) {
        return { layout: layout ?? [], staleToLiveFaceIds };
    }

    const viewerRoofs = viewer.roofMeshArr;
    const staleFaceIdToRoofNum = buildStaleFaceIdToRoofNumMap(layoutRoofSummaries);
    const usedLiveFaceIds = new Set<string>();
    const legacyByCount = buildLegacyModuleCountMap(viewerRoofs, layout, usedLiveFaceIds);

    const remapped: SavedRoofModulesLayout = [];
    for (const entry of layout) {
        if (!Array.isArray(entry?.moduleArr) || !entry.moduleArr.length) continue;

        let liveRoof = findViewerRoofForLayoutEntry(viewerRoofs, entry, staleFaceIdToRoofNum);
        if (!liveRoof && entry.roofFaceId) {
            const legacyLiveId = legacyByCount.get(entry.roofFaceId);
            if (legacyLiveId) {
                liveRoof = viewerRoofs.find((roof) => roof.roofFaceId === legacyLiveId);
            }
        }

        const liveFaceId = liveRoof?.roofFaceId;
        if (!liveFaceId) continue;
        usedLiveFaceIds.add(liveFaceId);
        if (entry.roofFaceId && entry.roofFaceId !== liveFaceId) {
            staleToLiveFaceIds.set(entry.roofFaceId, liveFaceId);
        }

        const keys = readViewerRoofStableKeys(liveRoof);
        remapped.push({
            ...entry,
            roofFaceId: liveFaceId,
            roofTagId: keys.roofTagId ?? entry.roofTagId,
            roofNumber: keys.roofNumber ?? entry.roofNumber,
            nameOverride: keys.nameOverride ?? entry.nameOverride,
        });
    }

    return { layout: remapped, staleToLiveFaceIds };
}

/** Resolve cached roof-face id lists (AI combos / winners) onto live viewer roofs. */
export function remapRoofFaceIdsToViewer(
    viewer: { roofMeshArr?: ViewerRoof[] } | null | undefined,
    staleRoofFaceIds: string[] | undefined,
    modulesLayout?: SavedRoofModulesLayout | null,
    layoutRoofSummaries?: LayoutRoofSummary[] | null,
): string[] {
    if (!Array.isArray(staleRoofFaceIds) || !staleRoofFaceIds.length) return [];
    if (!viewer?.roofMeshArr?.length) return staleRoofFaceIds.filter(Boolean);

    const { staleToLiveFaceIds } = remapModulesLayoutToViewerWithMap(
        viewer,
        modulesLayout,
        layoutRoofSummaries,
    );
    const staleFaceIdToRoofNum = buildStaleFaceIdToRoofNumMap(layoutRoofSummaries);

    return staleRoofFaceIds
        .map((staleId) => {
            if (staleToLiveFaceIds.has(staleId)) return staleToLiveFaceIds.get(staleId)!;
            if (viewer.roofMeshArr!.some((roof) => roof.roofFaceId === staleId)) return staleId;
            const roofNum = staleFaceIdToRoofNum.get(staleId);
            if (roofNum != null) {
                const live = viewer.roofMeshArr!.find(
                    (roof) =>
                        roof.userData?.roof_tag_id === roofNum ||
                        roof.userData?.roof_number_from_aurora === roofNum ||
                        roof.userData?.nameOverride === roofNum,
                );
                if (live?.roofFaceId) return live.roofFaceId;
            }
            return null;
        })
        .filter((id): id is string => typeof id === 'string' && id.length > 0);
}

export function countModulesApplicableInRemappedLayout(layout: SavedRoofModulesLayout | null | undefined): number {
    if (!Array.isArray(layout)) return 0;
    return layout.reduce(
        (sum, entry) => sum + (Array.isArray(entry?.moduleArr) ? entry.moduleArr.length : 0),
        0,
    );
}
